import { RadioCard } from "components/common/RadioCard";
import { RepaymentDetails } from "components/common/RepaymentDetails";
import {
  findRepaymentOption,
  RepaymentOption,
  RepaymentOptionType,
} from "domain/indicativeRepaymentOptions";
import { SelectedTreatmentsDetails } from "domain/selectedTreatments";
import { useCallback } from "react";
import {
  CIRH_EXTEND_TERM,
  CIRH_OPTIONS_HEADING,
  CIRH_OPTION_HEADING,
  CIRH_WITHOUT_EXTEND_TERM,
} from "./RequestRepaymentHoliday.const";

type ExtendTermCIRHProps = {
  extendTerm: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

const ExtendTermCIRH = ({
  extendTerm,
  selectedTreatments,
  onChange,
}: ExtendTermCIRHProps) => {
  const isSelected =
    extendTerm.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const handleChange = useCallback(
    () =>
      onChange({
        ...selectedTreatments,
        selectedOptionType: extendTerm.optionType,
        repaymentOption: extendTerm,
      }),
    [selectedTreatments, onChange, extendTerm]
  );
  return (
    <>
      <RadioCard
        name="cirhOptions"
        isSelected={isSelected}
        value={extendTerm.optionType}
        onChange={handleChange}
        label={CIRH_EXTEND_TERM}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={extendTerm}
          />
        )}
      </RadioCard>
    </>
  );
};
type JustCIRHProps = {
  justCIRH: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

const JustCIRH = ({
  justCIRH,
  selectedTreatments,
  onChange,
}: JustCIRHProps) => {
  const isSelected =
    justCIRH.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const handleChange = useCallback(
    () =>
      onChange({
        ...selectedTreatments,
        selectedOptionType: justCIRH.optionType,
        repaymentOption: justCIRH,
      }),
    [selectedTreatments, onChange, justCIRH]
  );

  return (
    <>
      <RadioCard
        name="cirhOptions"
        isSelected={isSelected}
        value={justCIRH.optionType}
        onChange={handleChange}
        label={CIRH_WITHOUT_EXTEND_TERM}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={justCIRH}
          />
        )}
      </RadioCard>
    </>
  );
};
type CIRHOptionsProps = {
  repaymentOptions: RepaymentOption[];
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

export const CIRHOptions = ({
  repaymentOptions,
  selectedTreatments,
  onChange,
}: CIRHOptionsProps) => {
  const getRepaymentOption = findRepaymentOption(repaymentOptions);
  const extendTermCIRH = getRepaymentOption(RepaymentOptionType.CIRHExtendTerm);
  const justCIRH = getRepaymentOption(
    RepaymentOptionType.CIRHWithoutExtendTerm
  );
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const ONE_OPTION_FLAG =
    existingLoanDetails != null
      ? existingLoanDetails.numberOfRepayments < 7
      : false;

  return (
    <div className="selected-options section-margin">
      <label id="cirh-repaymentOptions" className="title">
        {ONE_OPTION_FLAG ? CIRH_OPTION_HEADING : CIRH_OPTIONS_HEADING}
      </label>
      <fieldset aria-labelledby="repaymentOptions">
        {!!extendTermCIRH && (
          <ExtendTermCIRH
            extendTerm={extendTermCIRH}
            selectedTreatments={selectedTreatments}
            onChange={onChange}
          />
        )}
        {!!justCIRH && (
          <JustCIRH
            justCIRH={justCIRH}
            selectedTreatments={selectedTreatments}
            onChange={onChange}
          />
        )}
      </fieldset>
    </div>
  );
};
